import { TextArea } from '@yembo/yemblocks-core';
import { isEmptyStringOrWhitespace } from '@yembo/yemblocks-core/dist/_util';
import { Dispatch, useContext, useEffect, useState } from 'react';
import { AuthUserContext } from 'src/react/_shared/Context/AuthUserContext';
import { getIsDateInPast } from 'src/react/_util/dates/getDate';
import { getIsNoReviewCompany } from 'src/react/_util/getIsNoReviewCompany';
import { ContactJobState, ContactComponentState, CreateContactFormData } from '../..';
import { disableJobCreationCompanyKeys } from './_shared';
import { EmployeeSection } from './EmployeeSection';
import { JobSection } from './JobSection';
import { ContactSection } from './ContactSection';
import { LocationSection } from './LocationSection';
import { SurveySection } from './SurveySection';

type Props = {
  jobState: ContactJobState;
  state: ContactComponentState;
  formData: CreateContactFormData;
  shouldIncludeSampleSurvey?: boolean;
  dispatchState: Dispatch<Partial<ContactComponentState>>;
  dispatchJobState: Dispatch<Partial<ContactJobState>>;
  dispatchFormData: Dispatch<Partial<CreateContactFormData>>;
  setHandleAnimationEnd?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
};

export const DrawerBody = ({
  state,
  formData,
  jobState: job,
  shouldIncludeSampleSurvey = false,
  dispatchState,
  dispatchFormData,
  dispatchJobState,
  setHandleAnimationEnd,
}: Props) => {
  const { activeRole } = useContext(AuthUserContext);

  const { company, shouldSendEmail, shouldSendSms, origin, destination, insured, validation } = formData;
  const { surveyType, notes } = state;
  const { givenName, familyName, email, phone } = insured;

  const dispatchInsured = (value: Partial<typeof insured>) => dispatchFormData({ insured: { ...insured, ...value } });

  const { expectedSurveyDate } = job;

  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const isSelfSurvey = surveyType === 'self_survey';
  const isSampleSurvey = surveyType.includes('sample');
  const isNoReviewCompany = getIsNoReviewCompany(activeRole, company);
  const isDisableJobCreationCompany = disableJobCreationCompanyKeys.has(company.key);

  useEffect(() => {
    if (isSampleSurvey) {
      // don't need to enter any form information if creating a sample survey
      dispatchState({ isFormValid: true });
    } else {
      const isPhoneAndChecked = !phone || isPhoneValid;
      const isExpSurveyDateValid = !expectedSurveyDate || !getIsDateInPast(expectedSurveyDate);

      let isFormValid =
        !isEmptyStringOrWhitespace(givenName) &&
        !isEmptyStringOrWhitespace(familyName) &&
        isPhoneAndChecked &&
        validation.isEmailValid &&
        validation.isNumberOfBedsValid &&
        validation.isCompanySelected &&
        isExpSurveyDateValid &&
        !isDisableJobCreationCompany;

      if (isNoReviewCompany) {
        // require either a phone or email - can't create a manual survey
        const willSendEmailInvite = shouldSendEmail && !!email;
        const willSendPhoneInvite = shouldSendSms && !!phone;
        isFormValid = isFormValid && (willSendEmailInvite || willSendPhoneInvite);
      }

      dispatchState({ isFormValid });
    }
  }, [
    isPhoneValid,
    phone,
    email,
    validation,
    dispatchState,
    givenName,
    familyName,

    isSampleSurvey,
    expectedSurveyDate,
    isNoReviewCompany,
    isDisableJobCreationCompany,
    shouldSendSms,
    shouldSendEmail,
  ]);

  useEffect(
    () => dispatchFormData({ shouldSendEmail: !!email && validation.isEmailValid }),
    [email, validation.isEmailValid, dispatchFormData]
  );

  useEffect(
    () => dispatchFormData({ shouldSendSms: !!phone && isPhoneValid }),
    [phone, dispatchFormData, isPhoneValid]
  );

  return (
    <>
      <EmployeeSection formData={formData} dispatchFormData={dispatchFormData} dispatchJobState={dispatchJobState} />

      <ContactSection
        dispatchInsured={dispatchInsured}
        dispatchFormData={dispatchFormData}
        dispatchJobState={dispatchJobState}
        formData={formData}
        job={job}
        isNoReviewCompany={isNoReviewCompany}
        setIsPhoneValid={setIsPhoneValid}
        setHandleAnimationEnd={setHandleAnimationEnd}
      />

      <JobSection
        dispatchInsured={dispatchInsured}
        dispatchFormData={dispatchFormData}
        dispatchJobState={dispatchJobState}
        formData={formData}
        isSelfSurvey={isSelfSurvey}
        job={job}
      />

      <SurveySection
        dispatchFormData={dispatchFormData}
        dispatchJobState={dispatchJobState}
        dispatchState={dispatchState}
        formData={formData}
        isNoReviewCompany={isNoReviewCompany}
        isPhoneValid={isPhoneValid}
        isSelfSurvey={isSelfSurvey}
        job={job}
        shouldIncludeSampleSurvey={shouldIncludeSampleSurvey}
        state={state}
      />

      <TextArea
        label='Notes'
        className='new-job-note'
        rows={2}
        value={notes}
        maxLength={1000}
        onChange={(event) => dispatchState({ notes: event.target.value })}
      />

      <LocationSection
        location={origin}
        dispatch={(location) => dispatchFormData({ origin: { ...origin, ...location } })}
        header='Origin'
        defaultCountry={company.defaultCountry}
        className='origin-section'
      />

      <LocationSection
        location={destination}
        dispatch={(location) => dispatchFormData({ destination: { ...destination, ...location } })}
        header='Destination'
        defaultCountry={company.defaultCountry}
        className='destination-section'
      />
    </>
  );
};
